import React, { useCallback } from 'react';
import { ServicesContext } from '~/services';
import { defaultApiResponseHandler } from '~/util';

export interface ReferringProviderType {
    id: number;
    npi: string | null;
    first_name: string | null;
    last_name: string | null;
    name_prefix: string | null;
    name_suffix: string | null;
    enabled: boolean;
}

export type GetReferringProvidersReturnType = {
    data: {
        providers: Array<ReferringProviderType>;
    };
};

export interface ReferringProviderOptionType {
    value: number;
    label: string;
    name: string;
}

export function formatReferringProviderName(provider: ReferringProviderType): string {
    const name = [provider.name_prefix, provider.first_name, provider.last_name]
        .filter(Boolean)
        .join(' ');

    return provider.name_suffix ? `${name}, ${provider.name_suffix}` : name;
}

export function formatReferringProviderLabel(provider: ReferringProviderType): string {
    const name = formatReferringProviderName(provider);
    const label = name || `Provider #${provider.id}`;

    return provider.npi ? `${label} (NPI: ${provider.npi})` : label;
}

export const useGetReferringProviders = () => {
    const services = React.useContext(ServicesContext);

    const getReferringProviders = useCallback(async () => {
        try {
            const result: GetReferringProvidersReturnType = await services.api.call(
                {
                    method: 'get',
                    url: '/core-api/providers',
                },
                { handledErrors: true }
            );
            return result.data.providers;
        } catch (error) {
            throw defaultApiResponseHandler(error as CustomError);
        }
    }, [services.api]);

    return getReferringProviders;
};
